// Client-side validation for the customer-care forms (Query, Complaint, Suggestion).
// Mirrors Email_service/middleware/validateFeedback.js, which re-checks everything on the server.

// Letters, numbers, spaces and , . / - only (e.g. "A-12/3, Sector 5")
const ADDRESS = /^[\p{L}\p{N} ,./-]+$/u;
const ADDRESS_HINT = "can contain only letters, numbers, spaces and , . / -";

// Free text: normal punctuation is allowed, but not characters used in code or markup
const STATEMENT = /^[^<>{}[\]\\`$^*|~=]*$/;

// Keyed by formData field names. Fields a form doesn't have are skipped.
const FIELD_RULES = {
  fullName: { label: "Full Name", required: true, max: 100, pattern: /^\p{L}[\p{L} .]*$/u, hint: "can contain only letters, spaces and ." },
  flat: { label: "Flat/Door/Block No.", max: 100, pattern: ADDRESS, hint: ADDRESS_HINT },
  building: { label: "Building/Premises", max: 100, pattern: ADDRESS, hint: ADDRESS_HINT },
  village: { label: "Village", max: 100, pattern: ADDRESS, hint: ADDRESS_HINT },
  road: { label: "Road/Street/Lane", max: 100, pattern: ADDRESS, hint: ADDRESS_HINT },
  locality: { label: "Area/Locality", max: 100, pattern: ADDRESS, hint: ADDRESS_HINT },
  city: { label: "Town/City/District", max: 50, pattern: /^\p{L}[\p{L} .-]*$/u, hint: "can contain only letters, spaces and . -" },
  state: { label: "State", required: true, max: 50 },
  pin: { label: "PIN", max: 6, pattern: /^[1-9][0-9]{5}$/, hint: "must be a 6-digit number" },
  phone: { label: "Telephone No.", max: 16, pattern: /^\+?[0-9]{6,15}$/, hint: "must be 6–15 digits" },
  email: { label: "Email ID", required: true, max: 254, pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, hint: "is invalid" },
  pan: { label: "PAN", max: 10, pattern: /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/, hint: "must be in the format ABCDE1234F" },
  complaintArea: { label: "Area", max: 100 },
  complaint: { label: "Description", max: 2000, pattern: STATEMENT, hint: "must not contain < > { } [ ] \\ ` $ ^ * | ~ =" },
};

// Server error fields (payload names) -> formData names. The Complaint form overrides flat/building.
const SERVER_FIELDS = {
  name: "fullName",
  area: "locality",
  telephone: "phone",
  feedbackArea: "complaintArea",
  statement: "complaint",
};

// Returns { field: message } for every invalid field, or null if the form is valid.
export const validateFeedbackForm = (formData, enterCaptchaCode) => {
  const errors = {};

  for (const [key, rule] of Object.entries(FIELD_RULES)) {
    if (!(key in formData)) continue;

    const value = String(formData[key] ?? "").trim();
    if (!value) {
      if (rule.required) errors[key] = `${rule.label} is required`;
      continue;
    }
    if (value.length > rule.max) errors[key] = `${rule.label} must be at most ${rule.max} characters`;
    else if (rule.pattern && !rule.pattern.test(value)) errors[key] = `${rule.label} ${rule.hint || "is invalid"}`;
  }

  if (formData.consent !== true && formData.consent !== false) {
    errors.consent = 'Please select "Yes" or "No" for the consent statement';
  }

  const captcha = String(enterCaptchaCode ?? "").trim();
  if (!captcha) errors.captcha = "Please enter the captcha code";
  else if (captcha.length > 10) errors.captcha = "Captcha code is invalid";

  return Object.keys(errors).length ? errors : null;
};

// Converts a server error field to the formData field it belongs to.
export const toFormField = (serverField, overrides = {}) =>
  overrides[serverField] || SERVER_FIELDS[serverField] || serverField;

// Moves the cursor to the first invalid field so the user sees its message.
export const focusFirstError = (errors) => {
  const names = Object.keys(errors).map((field) => (field === "consent" ? "consentYes" : field));
  const first = names.find((name) => document.getElementsByName(name).length);
  if (first) document.getElementsByName(first)[0].focus();
};
